/**
 * Proposal Management Service
 * CRUD operations and business logic for sales proposals
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { logAuditEvent } from './auditLog';
import { getLead, updateLeadStatus } from './leadManagement';
import { sendEmailWithFirebase } from './emailService';
import {
  Proposal,
  ProposalCreateData,
  ProposalUpdateData,
  ProposalFilters,
  ProposalStats,
  ProposalPricing,
  getDefaultSections,
  getDefaultFeatures,
} from '../types/proposal';

const PROPOSALS_COLLECTION = 'proposals';

/**
 * Helper function to safely convert Timestamp or Date to Date
 */
const toDate = (value: any): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value && typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch (e) {
      return new Date();
    }
  }
  return new Date();
};

/**
 * Helper function to safely get milliseconds from Timestamp or Date
 */
const toMillis = (value: any): number => {
  const date = toDate(value);
  return date.getTime();
};

/**
 * Get single proposal by ID
 */
export const getProposal = async (proposalId: string): Promise<Proposal> => {
  try {
    const docRef = doc(db, PROPOSALS_COLLECTION, proposalId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Proposal not found');
    }

    const data = docSnap.data() as any;
    
    // Convert all Timestamp fields to Date
    const convertTimestamp = (value: any): any => {
      if (value && typeof value === 'object') {
        if ('toDate' in value && typeof value.toDate === 'function') {
          try {
            return value.toDate();
          } catch (e) {
            return value;
          }
        }
      }
      return value;
    };
    
    return {
      id: docSnap.id,
      ...data,
      validUntil: convertTimestamp(data.validUntil),
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
      sentAt: convertTimestamp(data.sentAt),
    } as Proposal;
  } catch (error) {
    console.error('Error fetching proposal:', error);
    throw new Error('Failed to fetch proposal');
  }
};

/**
 * Get proposals with optional filtering
 */
export const getProposals = async (
  filters: ProposalFilters = {}
): Promise<{ proposals: Proposal[]; total: number }> => {
  try {
    let q = query(collection(db, PROPOSALS_COLLECTION));

    // Apply filters
    if (filters.leadId) {
      q = query(q, where('leadId', '==', filters.leadId));
    }
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    if (filters.template) {
      q = query(q, where('template', '==', filters.template));
    }
    if (filters.createdBy) {
      q = query(q, where('createdBy', '==', filters.createdBy));
    }
    if (filters.sentAfter) {
      q = query(q, where('sentAt', '>=', filters.sentAfter));
    }
    if (filters.sentBefore) {
      q = query(q, where('sentAt', '<=', filters.sentBefore));
    }

    // Apply sorting
    const sortBy = filters.sortBy || 'createdAt';
    const sortOrder = filters.sortOrder || 'desc';
    q = query(q, orderBy(sortBy, sortOrder));

    // Apply limit
    if (filters.limit) {
      q = query(q, firestoreLimit(filters.limit));
    }

    const querySnapshot = await getDocs(q);
    const proposals = querySnapshot.docs.map((doc) => {
      const data = doc.data() as any;
      
      // Helper to convert Timestamp to Date
      const convertTimestamp = (value: any): any => {
        if (value && typeof value === 'object') {
          if ('toDate' in value && typeof value.toDate === 'function') {
            try {
              return value.toDate();
            } catch (e) {
              return value;
            }
          }
        }
        return value;
      };
      
      return {
        id: doc.id,
        ...data,
        validUntil: convertTimestamp(data.validUntil),
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
        sentAt: convertTimestamp(data.sentAt),
      } as Proposal;
    });

    // Apply search filter in memory (Firestore doesn't support full-text search)
    let filteredProposals = proposals;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filteredProposals = proposals.filter(
        (proposal) =>
          proposal.leadName.toLowerCase().includes(searchLower) ||
          proposal.leadEmail.toLowerCase().includes(searchLower) ||
          proposal.leadCompany?.toLowerCase().includes(searchLower) ||
          proposal.title.toLowerCase().includes(searchLower)
      );
    }

    return {
      proposals: filteredProposals,
      total: filteredProposals.length,
    };
  } catch (error) {
    console.error('Error fetching proposals:', error);
    throw new Error('Failed to fetch proposals');
  }
};

/**
 * Create a new proposal
 */
export const createProposal = async (
  data: ProposalCreateData,
  createdBy: string,
  createdByName: string
): Promise<Proposal> => {
  try {
    console.log('createProposal called with:', { createdBy, createdByName, leadId: data.leadId });
    
    // Validate user credentials
    if (!createdBy || !createdByName) {
      console.error('CRITICAL: User credentials missing:', { createdBy, createdByName });
      throw new Error('User credentials are required to create a proposal');
    }

    const now = Timestamp.now();

    // Get lead information
    const lead = await getLead(data.leadId);

    if (!lead) {
      throw new Error('Lead not found');
    }

    // Calculate validity
    const validityDays = data.validityDays || 30;
    const validUntil = Timestamp.fromDate(
      new Date(Date.now() + validityDays * 24 * 60 * 60 * 1000)
    );

    // Get default sections and features based on template
    const template = data.template || 'standard';
    const sections = data.customSections
      ? getDefaultSections(template).map((section) => {
          const custom = data.customSections?.find((s) => s.id === section.id);
          return custom ? { ...section, ...custom } : section;
        })
      : getDefaultSections(template);

    const features = getDefaultFeatures(data.pricing.pricingTier);

    // Helper function to ensure no undefined values
    const getNumber = (value: any, defaultValue: number = 0): number => {
      const parsed = Number(value);
      return isNaN(parsed) || value === undefined || value === null ? defaultValue : parsed;
    };

    const getString = (value: any, defaultValue: string = ''): string => {
      return value === undefined || value === null || value === '' ? defaultValue : String(value);
    };

    // Bulletproof: Validate input pricing object exists and has no undefined fields
    const inputPricing = (data.pricing || {}) as Partial<ProposalPricing>;
    console.log('Raw pricing input:', inputPricing);
    
    // Validate each field explicitly - this catches undefined before object construction
    const doorCountValue = inputPricing.doorCount;
    const pricingTierValue = inputPricing.pricingTier;
    const basePriceValue = inputPricing.basePrice;
    const discountPercentValue = inputPricing.discountPercent;
    const discountAmountValue = inputPricing.discountAmount;
    const finalPriceValue = inputPricing.finalPrice;
    const paymentScheduleValue = inputPricing.paymentSchedule;
    const contractTermValue = inputPricing.contractTerm;
    
    console.log('Individual field values - doorCount:', doorCountValue, 'type:', typeof doorCountValue);
    console.log('Individual field values - discountPercent:', discountPercentValue, 'type:', typeof discountPercentValue);
    console.log('Individual field values - discountAmount:', discountAmountValue, 'type:', typeof discountAmountValue);

    // Build pricing object with absolute guarantees about field types and no undefined
    const pricingObject: ProposalPricing = {
      doorCount: getNumber(doorCountValue, 0),
      pricingTier: getString(pricingTierValue, '10-20') as any,
      basePrice: getNumber(basePriceValue, 0),
      discountPercent: getNumber(discountPercentValue, 0),
      discountAmount: getNumber(discountAmountValue, 0),
      finalPrice: getNumber(finalPriceValue, 0),
      paymentSchedule: getString(paymentScheduleValue, 'annual') as any,
      contractTerm: getNumber(contractTermValue, 12),
    };

    console.log('Backend pricing object after sanitization:', pricingObject);
    
    // Final validation: ensure NO undefined values exist in the pricing object
    Object.entries(pricingObject).forEach(([key, value]) => {
      if (value === undefined) {
        console.error(`CRITICAL: Pricing field "${key}" is still undefined after sanitization!`);
        throw new Error(`Pricing field "${key}" cannot be undefined`);
      }
    });
    
    // Additional check: JSON stringify and parse to catch any hidden undefined values
    try {
      const jsonStr = JSON.stringify(pricingObject);
      console.log('Pricing object as JSON:', jsonStr);
      
      if (jsonStr.includes('undefined')) {
        console.error('CRITICAL: JSON contains undefined:', jsonStr);
        throw new Error('Pricing object contains undefined values in JSON serialization');
      }
      
      const reparsed = JSON.parse(jsonStr);
      console.log('Pricing object after JSON round-trip:', reparsed);
    } catch (jsonError) {
      console.error('Error during JSON validation:', jsonError);
      throw jsonError;
    }

    const proposalData: Omit<Proposal, 'id'> = {
      leadId: data.leadId,
      leadName: lead.contactInfo.name,
      leadEmail: lead.contactInfo.email,
      leadCompany: lead.contactInfo.company,
      status: 'draft',
      template,
      language: 'en', // Default language
      title: `Proposal for ${lead.contactInfo.company || lead.contactInfo.name}`,
      sections,
      features,
      pricing: pricingObject,
      roiCalculation: data.roiCalculation,
      painPointsAddressed: lead.businessInfo.painPoints || [],
      validUntil,
      metrics: {
        viewCount: 0,
        downloadCount: 0,
        sharedCount: 0,
      },
      signatureRequired: data.signatureRequired ?? true,
      createdAt: now,
      createdBy,
      createdByName,
      updatedAt: now,
      notes: data.notes,
    };

    // CRITICAL: Remove ALL undefined values from the entire object recursively before Firestore submission
    const removeUndefinedValues = (obj: any): any => {
      if (obj === null || obj === undefined) return null;
      if (typeof obj !== 'object') return obj;
      if (Array.isArray(obj)) {
        return obj.map(item => removeUndefinedValues(item)).filter(item => item !== undefined);
      }
      
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          if (typeof value === 'object' && value !== null) {
            const cleanedValue = removeUndefinedValues(value);
            if (cleanedValue !== undefined) {
              cleaned[key] = cleanedValue;
            }
          } else {
            cleaned[key] = value;
          }
        }
      }
      return cleaned;
    };

    const cleanedProposalData = removeUndefinedValues(proposalData);
    console.log('✅ After removeUndefinedValues:', cleanedProposalData);
    
    // DOUBLE CHECK: Ensure roiCalculation and pricing are properly formed
    if (!cleanedProposalData.roiCalculation || Object.keys(cleanedProposalData.roiCalculation).length === 0) {
      console.warn('⚠️ WARNING: roiCalculation was removed or is empty, using fallback');
      cleanedProposalData.roiCalculation = {
        currentMonthlyCost: 0,
        proposedMonthlyCost: 0,
        monthlySavings: 0,
        annualSavings: 0,
        timeCurrentlySpent: 0,
        timeWithDossiLoc: 0,
        timeSaved: 0,
        hourlyRate: 0,
        timeSavingsValue: 0,
        totalMonthlyValue: 0,
        totalAnnualValue: 0,
        roiPercentage: 0,
        paybackPeriod: 0,
      };
    }

    // TRIPLE CHECK: Ensure pricing fields are NEVER undefined - this is the critical fix
    if (cleanedProposalData.pricing) {
      console.log('🔍 Checking pricing object fields before final assignment:');
      Object.entries(cleanedProposalData.pricing).forEach(([key, value]) => {
        console.log(`  ${key}: ${value} (type: ${typeof value})`);
      });

      cleanedProposalData.pricing = {
        doorCount: cleanedProposalData.pricing.doorCount ?? 0,
        pricingTier: cleanedProposalData.pricing.pricingTier ?? '10-20',
        basePrice: cleanedProposalData.pricing.basePrice ?? 0,
        discountPercent: cleanedProposalData.pricing.discountPercent ?? 0,
        discountAmount: cleanedProposalData.pricing.discountAmount ?? 0,
        finalPrice: cleanedProposalData.pricing.finalPrice ?? 0,
        paymentSchedule: cleanedProposalData.pricing.paymentSchedule ?? 'annual',
        contractTerm: cleanedProposalData.pricing.contractTerm ?? 12,
      };
      
      console.log('✅ Final pricing object after re-guarantee:', cleanedProposalData.pricing);
    } else {
      console.error('❌ CRITICAL: Pricing object is missing entirely!');
      throw new Error('Pricing object is missing from proposal data');
    }
    
    // Log for debugging
    console.log('✅ Proposal pricing before addDoc:', cleanedProposalData.pricing);
    console.log('Full proposal data structure:', JSON.stringify(cleanedProposalData, null, 2));

    // Final check: ensure pricing is properly formed before sending to Firestore
    if (!cleanedProposalData.pricing || typeof cleanedProposalData.pricing !== 'object') {
      throw new Error('Pricing object is not properly formed after cleaning');
    }

    // Validate that pricing has all required fields
    const requiredPricingFields = ['doorCount', 'pricingTier', 'basePrice', 'discountPercent', 'discountAmount', 'finalPrice', 'paymentSchedule', 'contractTerm'];
    for (const field of requiredPricingFields) {
      if (!(field in cleanedProposalData.pricing)) {
        console.error(`CRITICAL: Required pricing field "${field}" is missing!`);
        throw new Error(`Required pricing field "${field}" is missing from pricing object`);
      }
    }

    let docRef;
    try {
      console.log('📤 About to submit to Firestore with data:', {
        hasLeadId: !!cleanedProposalData.leadId,
        hasPricing: !!cleanedProposalData.pricing,
        pricingFields: cleanedProposalData.pricing ? Object.keys(cleanedProposalData.pricing) : [],
        hasRoiCalculation: !!cleanedProposalData.roiCalculation,
        createdBy: cleanedProposalData.createdBy,
        createdByName: cleanedProposalData.createdByName,
      });

      docRef = await addDoc(collection(db, PROPOSALS_COLLECTION), cleanedProposalData);
      console.log('✅ Successfully created proposal with ID:', docRef.id);
    } catch (firestoreError) {
      console.error('❌ Firestore addDoc error:', firestoreError);
      console.error('Error code:', (firestoreError as any)?.code);
      console.error('Error message:', (firestoreError as any)?.message);
      
      // Check for undefined values in the data that was submitted
      console.error('🔍 Analyzing data for undefined values:');
      const checkUndefined = (obj: any, path: string = ''): string[] => {
        const issues: string[] = [];
        for (const [key, value] of Object.entries(obj)) {
          const currentPath = path ? `${path}.${key}` : key;
          if (value === undefined) {
            issues.push(`${currentPath} is undefined`);
          } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            issues.push(...checkUndefined(value, currentPath));
          } else if (Array.isArray(value)) {
            value.forEach((item, idx) => {
              if (typeof item === 'object' && item !== null) {
                issues.push(...checkUndefined(item, `${currentPath}[${idx}]`));
              } else if (item === undefined) {
                issues.push(`${currentPath}[${idx}] is undefined`);
              }
            });
          }
        }
        return issues;
      };
      
      const undefinedIssues = checkUndefined(cleanedProposalData);
      if (undefinedIssues.length > 0) {
        console.error('Found undefined values:', undefinedIssues);
      } else {
        console.log('✅ No undefined values found in data');
      }
      
      console.error('Proposal data that failed:', JSON.stringify(cleanedProposalData, null, 2));
      throw firestoreError;
    }

    await logAuditEvent({
      action: 'proposal.create',
      resource: 'proposals',
      resourceId: docRef.id,
      details: {
        leadId: data.leadId,
        template,
        finalPrice: data.pricing.finalPrice,
      },
      performedBy: createdBy,
    });

    // Convert Timestamp to Date for the return value
    const returnData = { id: docRef.id, ...cleanedProposalData };
    if (returnData.validUntil && typeof returnData.validUntil === 'object' && 'toDate' in returnData.validUntil) {
      returnData.validUntil = (returnData.validUntil as any).toDate();
    }
    if (returnData.createdAt && typeof returnData.createdAt === 'object' && 'toDate' in returnData.createdAt) {
      returnData.createdAt = (returnData.createdAt as any).toDate();
    }
    if (returnData.updatedAt && typeof returnData.updatedAt === 'object' && 'toDate' in returnData.updatedAt) {
      returnData.updatedAt = (returnData.updatedAt as any).toDate();
    }
    return returnData;
  } catch (error) {
    console.error('Error creating proposal:', error);
    throw new Error('Failed to create proposal');
  }
};

/**
 * Update proposal
 */
export const updateProposal = async (
  proposalId: string,
  data: ProposalUpdateData,
  updatedBy: string
): Promise<void> => {
  try {
    await updateDoc(doc(db, PROPOSALS_COLLECTION, proposalId), {
      ...data,
      updatedAt: Timestamp.now(),
    });

    await logAuditEvent({
      action: 'proposal.update',
      resource: 'proposals',
      resourceId: proposalId,
      details: data,
      performedBy: updatedBy,
    });
  } catch (error) {
    console.error('Error updating proposal:', error);
    throw new Error('Failed to update proposal');
  }
};

/**
 * Send proposal to lead
 */
export const sendProposal = async (
  proposalId: string,
  sentBy: string,
  sentByName: string
): Promise<void> => {
  try {
    const proposal = await getProposal(proposalId);
    
    // Debug: Check validUntil type
    console.log('📊 Proposal validUntil type:', typeof proposal.validUntil);
    console.log('📊 Proposal validUntil value:', proposal.validUntil);
    console.log('📊 Is Date?', proposal.validUntil instanceof Date);
    console.log('📊 Has toDate method?', typeof (proposal.validUntil as any)?.toDate);

    await updateDoc(doc(db, PROPOSALS_COLLECTION, proposalId), {
      status: 'sent',
      sentAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    // Update lead status to proposal_sent
    await updateLeadStatus(
      proposal.leadId,
      'proposal_sent',
      sentBy,
      sentByName,
      `Proposal sent: ${proposal.title}`
    );

    await logAuditEvent({
      action: 'proposal.send',
      resource: 'proposals',
      resourceId: proposalId,
      details: {
        leadId: proposal.leadId,
        sentTo: proposal.leadEmail,
      },
      performedBy: sentBy,
    });

    // Send email with proposal link
    const baseUrl = process.env.PROPOSAL_VIEW_URL || 'https://dossiloc.ca';
    const proposalUrl = `${baseUrl}/view/proposal/${proposalId}`;
    
    const summarySection = proposal.sections.find(s => s.id === 'executive-summary');
    const summaryText = summarySection?.content || 'We believe our solution will help streamline your rental operations and improve tenant satisfaction.';
    
    const emailBody = `Hi ${proposal.leadName},

Thank you for your interest in DossiLoc! We've prepared a customized proposal for your property management needs.

**${proposal.title}**

${summaryText}

**View Your Proposal:** ${proposalUrl}

This proposal includes:
• Detailed pricing breakdown
• Feature comparison
• Implementation timeline
• ROI analysis

The proposal will remain valid until ${toDate(proposal.validUntil).toLocaleDateString('en-CA')}.

If you have any questions or would like to discuss the proposal, please don't hesitate to reach out.

Best regards,
${sentByName || 'DossiLoc Sales Team'}
sales@dossiloc.ca

---
DossiLoc - Complete rent collection automation for Canadian property managers`;

    const emailResult = await sendEmailWithFirebase({
      to: proposal.leadEmail,
      toName: proposal.leadName,
      subject: `Your Custom Proposal: ${proposal.title}`,
      body: emailBody,
      leadId: proposal.leadId,
      sentBy,
      sentByName,
    });

    if (!emailResult.success) {
      throw new Error(`Failed to send email: ${emailResult.error}`);
    }

    console.log('Proposal email sent successfully:', emailResult.messageId);
  } catch (error) {
    console.error('Error sending proposal:', error);
    throw new Error('Failed to send proposal');
  }
};

/**
 * Track proposal view
 */
export const trackProposalView = async (proposalId: string): Promise<void> => {
  try {
    const proposal = await getProposal(proposalId);
    const now = Timestamp.now();

    const updates: any = {
      'metrics.viewCount': proposal.metrics.viewCount + 1,
      'metrics.lastViewedAt': now,
      updatedAt: now,
    };

    if (!proposal.metrics.firstViewedAt) {
      updates['metrics.firstViewedAt'] = now;
    }

    // Update status to viewed if currently sent
    if (proposal.status === 'sent') {
      updates.status = 'viewed';
    }

    await updateDoc(doc(db, PROPOSALS_COLLECTION, proposalId), updates);
  } catch (error) {
    console.error('Error tracking proposal view:', error);
    // Don't throw - this is analytics, shouldn't break the flow
  }
};

/**
 * Track proposal download
 */
export const trackProposalDownload = async (proposalId: string): Promise<void> => {
  try {
    const proposal = await getProposal(proposalId);

    await updateDoc(doc(db, PROPOSALS_COLLECTION, proposalId), {
      'metrics.downloadCount': proposal.metrics.downloadCount + 1,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error tracking proposal download:', error);
  }
};

/**
 * Accept proposal
 */
export const acceptProposal = async (
  proposalId: string,
  signedBy: string,
  signatureData?: string
): Promise<void> => {
  try {
    const proposal = await getProposal(proposalId);
    const now = Timestamp.now();

    await updateDoc(doc(db, PROPOSALS_COLLECTION, proposalId), {
      status: 'accepted',
      acceptedAt: now,
      signedAt: now,
      signedBy,
      signatureData,
      updatedAt: now,
    });

    // Update lead to negotiating (will move to closed won when contract is signed)
    await updateLeadStatus(
      proposal.leadId,
      'negotiating',
      'system',
      'System',
      `Proposal accepted by ${signedBy}`
    );

    await logAuditEvent({
      action: 'proposal.accept',
      resource: 'proposals',
      resourceId: proposalId,
      details: {
        leadId: proposal.leadId,
        signedBy,
        finalPrice: proposal.pricing.finalPrice,
      },
      performedBy: signedBy,
    });
  } catch (error) {
    console.error('Error accepting proposal:', error);
    throw new Error('Failed to accept proposal');
  }
};

/**
 * Reject proposal
 */
export const rejectProposal = async (
  proposalId: string,
  reason: string,
  rejectedBy: string
): Promise<void> => {
  try {
    const proposal = await getProposal(proposalId);

    await updateDoc(doc(db, PROPOSALS_COLLECTION, proposalId), {
      status: 'rejected',
      rejectedAt: Timestamp.now(),
      rejectionReason: reason,
      updatedAt: Timestamp.now(),
    });

    await logAuditEvent({
      action: 'proposal.reject',
      resource: 'proposals',
      resourceId: proposalId,
      details: {
        leadId: proposal.leadId,
        reason,
      },
      performedBy: rejectedBy,
    });
  } catch (error) {
    console.error('Error rejecting proposal:', error);
    throw new Error('Failed to reject proposal');
  }
};

/**
 * Generate PDF for proposal
 */
export const generateProposalPDF = async (
  proposalId: string,
  generatedBy: string
): Promise<string> => {
  try {
    // TODO: Integrate with PDF generation service
    // This would use libraries like jsPDF, PDFKit, or cloud services like DocRaptor
    
    const pdfUrl = `https://storage.example.com/proposals/${proposalId}.pdf`;

    await updateDoc(doc(db, PROPOSALS_COLLECTION, proposalId), {
      pdfUrl,
      pdfGeneratedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    await logAuditEvent({
      action: 'proposal.generate_pdf',
      resource: 'proposals',
      resourceId: proposalId,
      details: { pdfUrl },
      performedBy: generatedBy,
    });

    return pdfUrl;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF');
  }
};

/**
 * Get proposal statistics
 */
export const getProposalStats = async (): Promise<ProposalStats> => {
  try {
    const { proposals } = await getProposals({});

    const stats: ProposalStats = {
      total: proposals.length,
      byStatus: {
        draft: 0,
        sent: 0,
        viewed: 0,
        accepted: 0,
        rejected: 0,
        expired: 0,
      },
      byTemplate: {
        standard: 0,
        premium: 0,
        enterprise: 0,
        custom: 0,
      },
      totalValue: 0,
      avgProposalValue: 0,
      avgTimeToAccept: 0,
      conversionRate: 0,
      avgViewCount: 0,
      avgTimeSpentViewing: 0,
    };

    let sentCount = 0;
    let acceptedCount = 0;
    let totalViewCount = 0;
    let totalTimeSpentViewing = 0;
    let totalTimeToAccept = 0;
    let acceptedWithTimeCount = 0;

    proposals.forEach((proposal) => {
      // Count by status
      stats.byStatus[proposal.status]++;

      // Count by template
      stats.byTemplate[proposal.template]++;

      // Calculate total value (only accepted proposals)
      if (proposal.status === 'accepted') {
        stats.totalValue += proposal.pricing.finalPrice;
        acceptedCount++;

        // Calculate time to accept
        if (proposal.sentAt && proposal.acceptedAt) {
          const days =
            (toMillis(proposal.acceptedAt) - toMillis(proposal.sentAt)) /
            (1000 * 60 * 60 * 24);
          totalTimeToAccept += days;
          acceptedWithTimeCount++;
        }
      }

      // Count sent proposals
      if (proposal.sentAt) {
        sentCount++;
      }

      // Track view metrics
      totalViewCount += proposal.metrics.viewCount;
      totalTimeSpentViewing += proposal.metrics.timeSpentViewing || 0;
    });

    // Calculate averages
    stats.avgProposalValue = acceptedCount > 0 ? stats.totalValue / acceptedCount : 0;
    stats.conversionRate = sentCount > 0 ? (acceptedCount / sentCount) * 100 : 0;
    stats.avgViewCount = proposals.length > 0 ? totalViewCount / proposals.length : 0;
    stats.avgTimeSpentViewing =
      proposals.length > 0 ? totalTimeSpentViewing / proposals.length : 0;
    stats.avgTimeToAccept =
      acceptedWithTimeCount > 0 ? totalTimeToAccept / acceptedWithTimeCount : 0;

    return stats;
  } catch (error) {
    console.error('Error calculating proposal stats:', error);
    throw new Error('Failed to calculate proposal stats');
  }
};

/**
 * Check and expire old proposals
 */
export const expireOldProposals = async (): Promise<number> => {
  try {
    const now = Timestamp.now();
    const { proposals } = await getProposals({
      status: 'sent',
    });

    let expiredCount = 0;

    for (const proposal of proposals) {
      if (toMillis(proposal.validUntil) < now.toMillis()) {
        await updateDoc(doc(db, PROPOSALS_COLLECTION, proposal.id), {
          status: 'expired',
          updatedAt: now,
        });
        expiredCount++;
      }
    }

    return expiredCount;
  } catch (error) {
    console.error('Error expiring proposals:', error);
    throw new Error('Failed to expire proposals');
  }
};
